import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";
import type {Message} from "@khan/guide-types";
import {containsDataURL} from "@khan/guide-utils";

import {ImagePreview} from "../image-attach/image-preview.tsx";

import {FullscreenImageModal} from "./fullscreen-image-modal.tsx";
import {ModeratedImage} from "./moderated-image.tsx";

import warningCircleIcon from "@phosphor-icons/core/regular/warning-circle.svg";

interface Props {
    // The message containing the image to display
    message: Message;
    // Optional display width for the preview (default: 128px)
    displayWidth?: number;
    // Optional display height for the preview (default: 128px)
    displayHeight?: number;
}

/**
 * Renders an image preview in the messages UI with fullscreen modal capability.
 */
export function ImagePreviewMessage({
    message,
    displayWidth = 128,
    displayHeight = 128,
}: Props): React.ReactElement | null {
    // Keep it simple: only render preview images for
    // user-generated messages for now.
    if (message.type !== "outgoing") {
        return null;
    }

    // We only support dataURL at the moment.
    if (!containsDataURL(message.text)) {
        return null;
    }

    const imageSource = message.attachedImage || message.text;

    return (
        <View>
            <View style={styles.previewMessage}>
                {message.contentFlagged ? (
                    <View>
                        <ModeratedImage testId="moderated-image-icon" />
                        <View style={styles.unableToUploadWrapper}>
                            <PhosphorIcon icon={warningCircleIcon} />
                            <Strut size={4} />
                            <LabelSmall>
                                <Trans>Unable to attach</Trans>
                            </LabelSmall>
                        </View>
                    </View>
                ) : (
                    <ModalLauncher
                        aria-label={t`View full size image`}
                        modal={
                            <FullscreenImageModal
                                image={imageSource}
                                altText={undefined}
                            />
                        }
                    >
                        {({openModal}) => (
                            <Clickable
                                testId="message-image-thumbnail"
                                style={styles.clickableContainer}
                                onClick={openModal}
                            >
                                {() => (
                                    <ImagePreview
                                        image={imageSource}
                                        displayHeight={displayHeight}
                                        displayWidth={displayWidth}
                                    />
                                )}
                            </Clickable>
                        )}
                    </ModalLauncher>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    previewMessage: {
        alignItems: "flex-end",
    },
    clickableContainer: {
        cursor: "pointer",
        // Add subtle hover effect
        ":hover": {
            opacity: 0.8,
        },
        // Add focus ring for accessibility
        ":focus": {
            outline: `2px solid ${semanticColor.focus.outer}`,
            outlineOffset: "2px",
        },
    },
    unableToUploadWrapper: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingBlockStart: spacing.xSmall_8,
        color: semanticColor.core.foreground.critical.default,
    },
});
